import React, { useEffect, useState } from 'react';
import {
  faPen,
  faRotateRight,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MainButton from '../../../components/agv-button';
import Status from '../../../components/agv-status';
import LogPayloadViewer from './LogPayloadViewer';
import LogHistoryTimeline from './LogHistoryTimeline';
import {
  formatAttempts,
  formatDateTime,
  getLastErrorMessage,
  getStopReasonLabel,
  getLogState,
  getLogStateDefinition,
  getLogStateLabel,
  LOG_STATE,
  canBeManuallyRetried,
} from '../logs.utils';

const TABS = { SUMMARY: 'SUMMARY', PAYLOAD: 'PAYLOAD', HISTORY: 'HISTORY' };

function Fact({ label, value }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <dt className="tw-text-xs tw-font-work tw-text-text-dark-medium-importance">
        {label}
      </dt>
      <dd className="tw-m-0 tw-text-sm">{value}</dd>
    </div>
  );
}

function StateBanner({ log }) {
  const state = getLogState(log);
  const errorMessage = getLastErrorMessage(log);
  const stopReasonLabel = getStopReasonLabel(log);

  if (state === LOG_STATE.INTERVENTION_REQUIRED) {
    return (
      <div className="tw-p-3 tw-rounded-md tw-bg-status-negative/10 tw-border tw-border-solid tw-border-status-negative/20">
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-status-negative">
          Action requise : Fleet ne fera plus d’essai automatique pour cet
          envoi.
        </p>
        {errorMessage && (
          <p className="tw-m-0 tw-mt-1 tw-text-sm tw-break-words">
            {errorMessage}
          </p>
        )}
        {stopReasonLabel && (
          <p className="tw-m-0 tw-mt-1 tw-text-sm">
            <span className="tw-font-semibold">Arrêt des essais : </span>
            {stopReasonLabel}
          </p>
        )}
        <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-text-dark-medium-importance">
          {log.last_error_if_any?.type === 'METIER'
            ? 'Corrigez les données signalées par l’AS400, puis relancez l’envoi.'
            : 'Relancez l’envoi plus tard : l’AS400 Cooperl n’a pas répondu correctement, aucune correction des données n’est nécessaire.'}
        </p>
      </div>
    );
  }
  if (state === LOG_STATE.AUTO_RETRY) {
    return (
      <div className="tw-p-3 tw-rounded-md tw-bg-status-warn/10 tw-border tw-border-solid tw-border-status-warn/20">
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-[#B25E00]">
          L’envoi a échoué, Fleet réessaiera automatiquement (
          {formatAttempts(log)} essais).
        </p>
        {errorMessage && (
          <p className="tw-m-0 tw-mt-1 tw-text-sm tw-break-words">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
  if (state === LOG_STATE.PENDING) {
    return (
      <div className="tw-p-3 tw-rounded-md tw-bg-status-info/10 tw-border tw-border-solid tw-border-status-info/20 tw-text-sm">
        {log.status === 'IN_PROGRESS'
          ? 'Envoi en cours vers l’AS400 Cooperl.'
          : 'Envoi prévu dans la minute.'}
      </div>
    );
  }
  return (
    <div className="tw-p-3 tw-rounded-md tw-bg-status-positive/10 tw-border tw-border-solid tw-border-status-positive/20 tw-text-sm">
      Données reçues par l’AS400 Cooperl
      {log.last_response_if_any?.message
        ? ` : ${log.last_response_if_any.message}`
        : '.'}
    </div>
  );
}

/**
 * Détail d'un envoi : état, actions (admins), données envoyées et historique.
 */
export default function LogDetailPanel({
  log,
  canManage,
  history,
  onClose,
  onRetry,
  onEditPayload,
}) {
  const [tab, setTab] = useState(TABS.SUMMARY);

  useEffect(() => {
    setTab(TABS.SUMMARY);
  }, [log?.id]);

  const definition = getLogStateDefinition(log);
  const isActionable = canBeManuallyRetried(log);
  const tabs = [
    { key: TABS.SUMMARY, label: 'Résumé' },
    { key: TABS.PAYLOAD, label: 'Données envoyées' },
    ...(canManage ? [{ key: TABS.HISTORY, label: 'Historique' }] : []),
  ];

  return (
    <aside
      className="tw-flex tw-flex-col tw-w-[46%] tw-shrink-0 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-black/10 tw-overflow-hidden"
      style={{ height: 'calc(100vh - 294px)', minHeight: '396px' }}
      aria-label={`Détail de l’envoi de la tournée ${log.tour_key}`}
    >
      <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4 tw-border-0 tw-border-b tw-border-solid tw-border-black/10">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
          <div className="tw-flex tw-flex-col tw-gap-1 tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
              {/* Pas de h2 : le style global des titres de l'application l'emporte sur les classes */}
              <p
                role="heading"
                aria-level={2}
                className="tw-m-0 tw-text-lg tw-leading-6 tw-font-semibold tw-truncate"
              >
                Tournée {log.tour_key}
              </p>
              <span className="tw-shrink-0 tw-whitespace-nowrap">
                <Status
                  status={definition?.status}
                  text={getLogStateLabel(log)}
                />
              </span>
            </div>
            <span className="tw-text-xs tw-text-text-dark-medium-importance">
              Réf. {log.id}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le détail"
            className="tw-p-1 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-text-dark-medium-importance hover:tw-text-text-dark-high-importance"
          >
            <FontAwesomeIcon className="tw-size-4" icon={faXmark} />
          </button>
        </div>

        {canManage && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <MainButton
              icon={faRotateRight}
              text="Relancer l’envoi"
              onClick={onRetry}
              disabled={!isActionable}
            />
            <MainButton
              variant="secondary"
              icon={faPen}
              text="Corriger les données"
              onClick={onEditPayload}
              disabled={!isActionable}
            />
            {!isActionable && (
              <span className="tw-text-xs tw-text-text-dark-medium-importance">
                Actions disponibles quand les essais automatiques sont terminés
                (action requise).
              </span>
            )}
          </div>
        )}

        <div role="tablist" className="tw-flex tw-gap-4 tw-border-0">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`tw-pb-1 tw-px-0 tw-bg-transparent tw-border-0 tw-border-b-2 tw-border-solid tw-text-sm tw-cursor-pointer ${
                tab === key
                  ? 'tw-border-main tw-font-semibold tw-text-text-dark-high-importance'
                  : 'tw-border-transparent tw-text-text-dark-medium-importance hover:tw-text-text-dark-high-importance'
              }`}
            >
              {label}
              {key === TABS.HISTORY && history.total
                ? ` (${history.total})`
                : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="tw-flex-1 tw-overflow-y-auto tw-p-4" role="tabpanel">
        {tab === TABS.SUMMARY && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <StateBanner log={log} />
            <dl className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 tw-m-0">
              <Fact
                label="Fin de tournée"
                value={formatDateTime(log.tour_signed_at)}
              />
              <Fact
                label="Données préparées le"
                value={formatDateTime(log.created_at)}
              />
              <Fact
                label="Dernière mise à jour"
                value={formatDateTime(log.updated_at)}
              />
              <Fact label="Essais d’envoi" value={formatAttempts(log)} />
              <Fact
                label="Nouvel essai automatique"
                value={
                  getLogState(log) === LOG_STATE.AUTO_RETRY ? 'Prévu' : 'Non'
                }
              />
            </dl>
          </div>
        )}
        {tab === TABS.PAYLOAD && <LogPayloadViewer datas={log.datas} />}
        {tab === TABS.HISTORY && canManage && (
          <LogHistoryTimeline history={history} />
        )}
      </div>
    </aside>
  );
}
